import { confirmar } from '@/components/ui/confirm-dialog';
import { PageHeader } from '@/components/patterns';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Sun, Search, Trash2, ArrowUpRight, Settings } from 'lucide-react';
import { AppLayout } from '@/components/layout/app-layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/lib/hooks/use-toast';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { pricerSolarService } from '../services/pricer-solar.service';
import type { PricerSolarQuote, PricerSolarStatus } from '../types/pricer-solar.types';
import { ETIQUETA_DE_ESTADO, VARIANTE_DE_ESTADO } from '../lib/estado-solar';

const STATUS_LABEL = ETIQUETA_DE_ESTADO;
const STATUS_VARIANT = VARIANTE_DE_ESTADO;

function fmtBs(n: number | null): string {
  if (n == null) return '—';
  return `Bs ${n.toLocaleString('es-BO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function fmtDate(s: string): string {
  return new Date(s).toLocaleDateString('es-BO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function PricerSolarListPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === 'COMPANY_ADMIN' || user?.role === 'PLATFORM_ADMIN';
  const [quotes, setQuotes] = useState<PricerSolarQuote[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<'__ALL__' | PricerSolarStatus>('__ALL__');
  const [search, setSearch] = useState('');

  const fetchQuotes = () => {
    setLoading(true);
    pricerSolarService
      .listQuotes({
        status: statusFilter === '__ALL__' ? undefined : statusFilter,
        search: search.trim() || undefined,
      })
      .then(setQuotes)
      .catch((err) => {
        toast({ title: 'Error al cargar', description: err?.message, variant: 'destructive' });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchQuotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchQuotes();
  };

  const handleDelete = async (q: PricerSolarQuote) => {
    if (q.status !== 'DRAFT') {
      toast({ title: 'Solo se pueden eliminar borradores', variant: 'destructive' });
      return;
    }
    if (
      !(await confirmar({
        title: `¿Eliminar la cotización ${q.code}?`,
        variant: 'destructive',
        confirmText: 'Sí, eliminar',
      }))
    )
      return;
    try {
      await pricerSolarService.deleteQuote(q.id);
      toast({ title: 'Cotización eliminada', variant: 'success' });
      fetchQuotes();
    } catch (err: any) {
      toast({ title: 'Error', description: err?.message, variant: 'destructive' });
    }
  };

  const stats = useMemo(() => {
    const total = quotes.length;
    const draft = quotes.filter((q) => q.status === 'DRAFT').length;
    const pending = quotes.filter((q) => q.status === 'PENDING_APPROVAL').length;
    const approved = quotes.filter((q) => q.status === 'APPROVED').length;
    const totalKwp = quotes.reduce((s, q) => s + (q.kwp || 0), 0);
    return { total, draft, pending, approved, totalKwp };
  }, [quotes]);

  return (
    <AppLayout>
      <div className="mx-auto max-w-[1440px] space-y-6 px-4 py-8 md:px-8">
        {/* El encabezado de Cotiza (rediseño, 19-oct). */}
        <PageHeader
          modulo="cotiza"
          etiqueta="Cotiza"
          icono={Sun}
          titulo="Cotizador Solar"
          subtitulo="Sistemas fotovoltaicos On-Grid · Bolivia"
          acciones={
            <>
              {isAdmin && (
                <Button variant="outline" onClick={() => navigate('/pricer-solar/config')}>
                  <Settings className="mr-2 h-4 w-4" />
                  Parámetros
                </Button>
              )}
              <Button
                variant="degradado"
                size="lg"
                className="px-5"
                onClick={() => navigate('/pricer-solar/new')}
              >
                <Plus className="mr-2 h-4 w-4" />
                Nueva cotización
              </Button>
            </>
          }
        />

        {/* Stats compactos */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Total</p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Borradores</p>
              <p className="text-2xl font-bold">{stats.draft}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Pendientes</p>
              <p className="text-2xl font-bold text-alerta">{stats.pending}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">kWp total</p>
              <p className="text-2xl font-bold text-ok">{stats.totalKwp.toFixed(1)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filtros + tabla */}
        <Card>
          <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle>Historial</CardTitle>
              <CardDescription>
                {quotes.length} cotización{quotes.length === 1 ? '' : 'es'}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <form onSubmit={handleSearchSubmit} className="flex items-center gap-1">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  <Input
                    placeholder="Buscar por código, cliente, empresa…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 w-72"
                  />
                </div>
              </form>
              <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
                <SelectTrigger className="w-44">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__ALL__">Todos los estados</SelectItem>
                  {(Object.keys(STATUS_LABEL) as PricerSolarStatus[]).map((s) => (
                    <SelectItem key={s} value={s}>
                      {STATUS_LABEL[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-12 text-center text-muted-foreground">Cargando…</div>
            ) : quotes.length === 0 ? (
              <div className="py-16 text-center space-y-3">
                <Sun className="w-12 h-12 mx-auto text-muted-foreground/40" />
                <p className="text-muted-foreground">
                  Todavía no creaste ninguna cotización solar, y nadie te compartió una.
                </p>
                <p className="text-sm text-muted-foreground">
                  Cada cotización la ve quien la creó. Si necesitás ver la de un compañero, pedile
                  que te la comparta desde el botón «Acceso» de la ficha.
                </p>
                <Button onClick={() => navigate('/pricer-solar/new')} size="sm">
                  <Plus className="w-4 h-4 mr-1.5" />
                  Crear la primera
                </Button>
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Código</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead className="text-right">kWp</TableHead>
                      <TableHead className="text-right">Paneles</TableHead>
                      <TableHead className="text-right">Inv. Neta</TableHead>
                      <TableHead className="text-right">ROI</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Fecha</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {quotes.map((q) => (
                      <TableRow
                        key={q.id}
                        className="cursor-pointer hover:bg-muted/40"
                        onClick={() => navigate(`/pricer-solar/${q.id}`)}
                      >
                        <TableCell className="whitespace-nowrap font-mono text-xs font-semibold">
                          {q.code}
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{q.clientName}</div>
                          {q.clientCompany && (
                            <div className="text-xs text-muted-foreground">{q.clientCompany}</div>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {q.kwp?.toFixed(2) ?? '—'}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {q.totalPaneles ?? '—'}
                        </TableCell>
                        <TableCell className="text-right font-mono">{fmtBs(q.invNetaBs)}</TableCell>
                        <TableCell className="text-right font-mono">
                          {q.roiAnios?.toFixed(1) ?? '—'}{' '}
                          <span className="text-xs text-muted-foreground">años</span>
                        </TableCell>
                        <TableCell>
                          <Badge variant={STATUS_VARIANT[q.status]}>{STATUS_LABEL[q.status]}</Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {fmtDate(q.createdAt)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div
                            className="flex items-center justify-end gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => navigate(`/pricer-solar/${q.id}`)}
                              title="Abrir"
                            >
                              <ArrowUpRight className="w-3.5 h-3.5" />
                            </Button>
                            {q.status === 'DRAFT' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDelete(q)}
                                title="Eliminar"
                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
